import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, History, Search } from "lucide-react";
import { listAudit, type AuditEntry, type AuditFilters } from "../api/audit";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { EmptyState } from "../components/EmptyState";
import { formatMoscowDateTime } from "../utils/datetime";

const ENTITY_TYPES = [
  { value: "", label: "Все объекты" },
  { value: "user", label: "Пользователи" },
  { value: "course", label: "Курсы" },
  { value: "module", label: "Модули" },
  { value: "lesson", label: "Уроки" },
  { value: "assignment", label: "Назначения" },
  { value: "quiz", label: "Тесты" },
  { value: "article", label: "Статьи БЗ" },
  { value: "category", label: "Категории БЗ" },
];

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const { user } = useCurrentUser();
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listAudit({
        ...filters,
        q: debouncedQ || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403)
        setError("Нет доступа — только super_admin/admin");
      else
        setError(
          err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
        );
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedQ, page]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user) return null;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-ink">Журнал действий</h1>
        <p className="text-sm text-ink-muted">
          Кто что менял в системе. Виден только администраторам.
        </p>
      </header>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search
            size={16}
            strokeWidth={2}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
          />
          <input
            type="search"
            placeholder="Поиск по логину, имени или действию…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-lg border border-line bg-white py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <select
          value={filters.entity_type ?? ""}
          onChange={(e) => {
            setFilters((f) => ({ ...f, entity_type: e.target.value || undefined }));
            setPage(1);
          }}
          className="input w-auto"
        >
          {ENTITY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="action prefix (например, course.)"
          value={filters.action ?? ""}
          onChange={(e) => {
            setFilters((f) => ({ ...f, action: e.target.value || undefined }));
            setPage(1);
          }}
          className="input w-56 font-mono text-xs"
        />
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="card p-12 text-center text-ink-muted">Загрузка…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={History}
          title="Записей не найдено"
          description="Попробуйте изменить фильтры."
        />
      ) : (
        <div className="overflow-hidden rounded-xl bg-white ring-1 ring-line">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-alt/40 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                <th className="px-4 py-2.5">Время</th>
                <th className="px-4 py-2.5">Кто</th>
                <th className="px-4 py-2.5">Действие</th>
                <th className="px-4 py-2.5">Объект</th>
                <th className="px-4 py-2.5">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const isOpen = expanded === e.id;
                return (
                  <Row
                    key={e.id}
                    entry={e}
                    isOpen={isOpen}
                    onToggle={() => setExpanded(isOpen ? null : e.id)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm text-ink-muted">
          <span>
            Страница {page} из {totalPages} · всего {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary inline-flex items-center gap-1 disabled:opacity-50"
            >
              <ChevronLeft size={14} strokeWidth={2.25} />
              Назад
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="btn-secondary inline-flex items-center gap-1 disabled:opacity-50"
            >
              Вперёд
              <ChevronRight size={14} strokeWidth={2.25} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  entry: e,
  isOpen,
  onToggle,
}: {
  entry: AuditEntry;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const hasPayload = Object.keys(e.payload).length > 0;
  const time = formatMoscowDateTime(e.created_at, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <>
      <tr
        onClick={hasPayload ? onToggle : undefined}
        className={`border-b border-line last:border-b-0 transition ${
          hasPayload ? "cursor-pointer hover:bg-surface-alt/30" : ""
        } ${isOpen ? "bg-surface-alt/30" : ""}`}
      >
        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-ink-muted">
          {time}
        </td>
        <td className="px-4 py-2.5">
          {e.actor_full_name ? (
            <div>
              <div className="font-medium text-ink">{e.actor_full_name}</div>
              <div className="font-mono text-[11px] text-ink-muted">
                {e.actor_login}
              </div>
            </div>
          ) : (
            <span className="text-ink-muted">— система —</span>
          )}
        </td>
        <td className="px-4 py-2.5 font-mono text-xs text-ink">{e.action}</td>
        <td className="px-4 py-2.5 text-xs text-ink-soft">
          {e.entity_type && (
            <span>
              {e.entity_type}
              {e.entity_id != null && (
                <span className="ml-1 text-ink-muted">#{e.entity_id}</span>
              )}
            </span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-ink-muted">
          {e.ip ?? ""}
        </td>
      </tr>
      {isOpen && hasPayload && (
        <tr className="border-b border-line bg-surface-alt/30">
          <td colSpan={5} className="px-4 py-3">
            <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg bg-white p-3 font-mono text-[11px] leading-relaxed text-ink-soft ring-1 ring-line">
              {JSON.stringify(e.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
